import {Button, CircularProgress} from '@mui/material';
import {DateTime} from 'luxon';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {getArea} from 'ol/extent';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {
  SearchQuery,
  useGetCatalogSearch,
  useGetCollection,
} from '@src/api/catalog';
import {useGetConfigurationCollections} from '@src/api/configurations';
import {notifyError} from '@src/components/notifier/notify';
import {WORLD_EXTENT} from '@src/constants';
import {ANALYZE_VISUALIZE_ROUTE} from '@src/constants/routes';
import {serialize as serializeDateTime} from '@src/hooks/useDateTimeParams';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions';
import {AppLocationState} from '@src/types/appLocation';
import {Collection} from '@src/types/configurations';
import {parseDateTimeFromCollectionWithDefaults} from '@src/utils/collections';

const parseExtentBboxFromCollection = (collection?: Collection) => {
  return collection?.extent?.spatial?.bbox?.[0] ?? WORLD_EXTENT;
};

export const VisualizeButton = () => {
  const navigate = useNavigate();
  const {
    collectionId,
    configurationId,
    serialize: serializeVisualizationParams,
  } = useVisualizationParams();
  const {collections} = useGetConfigurationCollections(configurationId);
  const selectedCollectionId = collectionId ?? collections[0]?.id;
  const searchParams = useOptimisticSearchParams();
  const {data: collection} = useGetCollection(selectedCollectionId);
  const collectionsBBox = parseExtentBboxFromCollection(collection);
  const collectionsDateRange =
    parseDateTimeFromCollectionWithDefaults(collection);
  const [searchQueryParams, setSearchQueryParams] = useState<
    SearchQuery | undefined
  >();

  const {data: searchResults, isLoading: isLoadingResults} =
    useGetCatalogSearch(searchQueryParams!, {enabled: !!searchQueryParams});

  const {bbox: firstResultBBox, properties} =
    searchResults?.features?.[0] || {};
  const firstResultDate = properties?.datetime;

  const isResultAreaMuchSmallerThanEntireCollection =
    firstResultBBox &&
    getArea(firstResultBBox) / getArea(collectionsBBox) < 0.001;
  const fitToExtent = isResultAreaMuchSmallerThanEntireCollection
    ? firstResultBBox
    : collectionsBBox;

  useEffect(() => {
    if (searchResults && fitToExtent?.length === 4) {
      try {
        const defaultVisualizeParameters = new URLSearchParams(
          serializeVisualizationParams({
            collectionId: selectedCollectionId,
            configurationId,
            layerId: '',
          }),
        );

        const dateTimeParams = firstResultDate
          ? new URLSearchParams(
              serializeDateTime({
                dateTimeFrom:
                  DateTime.fromISO(firstResultDate ?? '')
                    .startOf('day')
                    .toISO() ?? null,
                dateTimeTo:
                  DateTime.fromISO(firstResultDate ?? '')
                    .endOf('day')
                    .toISO() ?? null,
              }),
            )
          : new URLSearchParams();

        const navigateParams = new URLSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          ...Object.fromEntries(defaultVisualizeParameters.entries()),
          ...Object.fromEntries(dateTimeParams.entries()),
        });
        navigate(
          {
            pathname: `/${ANALYZE_VISUALIZE_ROUTE}`,
            search: navigateParams.toString(),
          },
          {
            state: {
              mapExtent: {
                bbox: fitToExtent,
                duration: 300,
              },
              tileBbox: fitToExtent,
            } satisfies AppLocationState,
          },
        );
      } catch (error) {
        notifyError({
          data: {
            message: 'Error fetching catalog data',
          },
        });
      } finally {
        setSearchQueryParams(undefined);
      }
    }
  }, [
    navigate,
    searchResults,
    searchParams,
    serializeVisualizationParams,
    selectedCollectionId,
    configurationId,
    fitToExtent,
    firstResultDate,
  ]);

  const isDisabled =
    !configurationId || !selectedCollectionId || isLoadingResults;
  return (
    <Button
      onClick={() => {
        if (!collection) {
          return;
        }
        setSearchQueryParams({
          bbox: collectionsBBox,
          datetime: `${collectionsDateRange.to.minus({months: 1}).toISO()}/${collectionsDateRange.to.toISO()}`,
          collections: [collection.id],
          limit: 10,
        });
      }}
      disabled={isDisabled}
      variant="contained"
      color="primary"
      size="large"
      sx={{
        marginBlockStart: 0.25,
        marginBlockEnd: 1,
        display: 'flex',
        justifyContent: 'center',
        marginInline: `${dimensions.leftSidebar.contentMargin}px`,
      }}
    >
      {isLoadingResults ? (
        <CircularProgress size={24} sx={{marginBlock: 0.15}} />
      ) : (
        'Visualize'
      )}
    </Button>
  );
};

export default VisualizeButton;
